<template>
  <div class="mensuracao-projeto" v-if="!carregando">
    <header class="cabecalho">
      <div>
        <router-link
          :to="{ name: 'gestao-projetos.detalhe', params: { idProjeto } }"
          class="voltar"
        >
          ← Voltar aos detalhes
        </router-link>
        <h2>Mensuração do projeto</h2>
        <p v-if="preparo && preparo.projeto" class="descricao">
          {{ preparo.projeto.titulo }} — registre o status das ações e o valor
          atual dos indicadores neste ciclo.
        </p>
      </div>
      <div class="cabecalho-acoes">
        <button
          type="button"
          class="botao-secundario"
          @click="carregarDados"
          :disabled="salvando"
        >
          Recarregar dados
        </button>
        <button
          type="button"
          class="botao-primario"
          @click="salvarMensuracao"
          :disabled="salvando"
        >
          {{ salvando ? "Salvando..." : "Salvar mensuração" }}
        </button>
      </div>
    </header>

    <div v-if="erro" class="erro">{{ erro }}</div>

    <form v-if="preparo" class="formulario" @submit.prevent="salvarMensuracao">
      <section class="bloco resumo-mensuracao">
        <header class="bloco-header">
          <h3>Resumo da mensuração</h3>
        </header>
        <div class="resumo-grid">
          <article class="resumo-item">
            <span>Status do projeto</span>
            <strong>{{ form.situacaoProjeto || "—" }}</strong>
          </article>
          <article class="resumo-item">
            <span>Progresso médio do projeto</span>
            <strong>{{ formatarPercentual(progressoMedioProjeto) }}</strong>
          </article>
          <article class="resumo-item" v-if="preparo && preparo.pdm">
            <span>
              Progresso médio do {{ nomePdm ? nomePdm : "PDM" }}
            </span>
            <strong>{{ formatarPercentual(progressoMedioPdm) }}</strong>
            <small v-if="preparo.pdm.projetos && preparo.pdm.projetos.length">
              Baseado em {{ preparo.pdm.projetos.length }} projetos vinculados
            </small>
          </article>
        </div>
      </section>
      <section class="bloco">
        <div class="linha">
          <label>
            Status do projeto
            <select
              v-model="form.situacaoProjeto"
              :class="classeObrigatorio(form.situacaoProjeto)"
            >
              <option value="">Selecione</option>
              <option
                v-for="situacao in opcoesSituacoes"
                :key="situacao"
                :value="situacao"
              >
                {{ situacao }}
              </option>
            </select>
          </label>
          <label>
            Data da mensuração
            <input type="date" v-model="form.dataReferencia" required />
          </label>
          <label>
            Título
            <input
              type="text"
              v-model="form.titulo"
              placeholder="Ex: Mensuração de Projeto X (01/01/2024)"
            />
          </label>
        </div>
        <label>
          Comentários gerais
          <textarea
            v-model="form.comentarios"
            rows="3"
            placeholder="Resumo ou evidências deste ciclo"
          ></textarea>
        </label>
      </section>

      <section class="bloco bloco-estruturas">
        <header class="bloco-header">
          <h3>Ações do projeto</h3>
          <small v-if="!form.acoes.length"
            >Nenhuma ação cadastrada para este projeto.</small
          >
        </header>
        <article
          v-for="acao in form.acoes"
          :key="acao.idMarco"
          class="cartao acao"
          :class="{ colapsado: acao.__colapsada }"
        >
          <header class="cartao-cabecalho">
            <div class="cartao-identificacao">
              <span class="badge badge-acao">Ação</span>
              <div class="cartao-textos">
                <strong>{{ acao.titulo || "Ação sem título" }}</strong>
                <p v-if="acao.descricao" class="descricao-acao">
                  {{ acao.descricao }}
                </p>
                <small>
                  Status atual: {{ acao.situacao || "—" }} • Progresso:
                  {{ formatarPercentual(acao.progresso) }}
                </small>
                <small v-if="acao.ultimaMensuracao">
                  Última mensuração em
                  {{ formatarDataCurta(acao.ultimaMensuracao.dataReferencia) }}
                  ({{ formatarPercentual(acao.ultimaMensuracao.progresso) }})
                </small>
              </div>
            </div>
            <button
              type="button"
              class="alternar-colapso"
              @click="alternarDetalhesAcao(acao)"
            >
              {{ acao.__colapsada ? "Expandir detalhes" : "Recolher detalhes" }}
            </button>
          </header>

          <div v-show="!acao.__colapsada" class="cartao-conteudo">
            <div class="grid-acao">
              <label>
                Status da ação
                <select
                  v-model="acao.situacao"
                  :class="classeObrigatorio(acao.situacao)"
                >
                  <option value="">Selecione</option>
                  <option
                    v-for="situacao in opcoesSituacoes"
                    :key="situacao"
                    :value="situacao"
                  >
                    {{ situacao }}
                  </option>
                </select>
              </label>
              <label>
                Progresso (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  v-model.number="acao.progresso"
                  @input="marcarAcaoManual(acao)"
                  @blur="confirmarProgressoAcao(acao)"
                />
              </label>
            </div>
            <label class="comentarios">
              Comentários
              <textarea
                v-model="acao.comentarios"
                rows="2"
                placeholder="Observações desta ação"
              ></textarea>
            </label>
            <div class="progresso-calculado">
              <small>
                Calculado pelas entregas:
                <strong>{{ formatarPercentual(acao.__progressoCalculado) }}</strong>
              </small>
              <button
                type="button"
                class="botao-link"
                @click="usarProgressoCalculadoAcao(acao)"
                v-if="acao.__progressoManual"
              >
                Usar valor calculado
              </button>
            </div>

            <section class="secao-entregas">
              <header class="secao-cabecalho">
                <h4>Entregas vinculadas</h4>
              </header>
              <article v-if="!acao.entregas.length" class="vazio interno">
                Nenhuma entrega cadastrada para esta ação.
              </article>
              <article
                v-for="entrega in acao.entregas"
                :key="entrega.idEntrega"
                class="cartao interno entrega"
                :class="{ colapsado: entrega.__colapsada }"
              >
                <header class="cartao-cabecalho entrega">
                  <div class="cartao-identificacao">
                    <span class="badge badge-entrega">Entrega</span>
                    <div class="cartao-textos">
                      <strong>{{ entrega.titulo || "Entrega sem título" }}</strong>
                      <small>
                        Status atual: {{ entrega.situacao || "—" }}
                      </small>
                      <small>
                        Peso: {{ formatarPercentual(entrega.peso) }} • Progresso:
                        {{ formatarPercentual(entrega.progresso) }}
                      </small>
                      <small v-if="entrega.ultimaMensuracao">
                        Última mensuração em
                        {{
                          formatarDataCurta(
                            entrega.ultimaMensuracao.dataReferencia
                          )
                        }}
                        ({{
                          formatarPercentual(entrega.ultimaMensuracao.progresso)
                        }})
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="alternar-colapso"
                    @click="alternarDetalhesEntrega(entrega)"
                  >
                    {{
                      entrega.__colapsada
                        ? "Expandir detalhes"
                        : "Recolher detalhes"
                    }}
                  </button>
                </header>

                <div v-show="!entrega.__colapsada" class="cartao-conteudo">
                  <p v-if="entrega.descricao" class="descricao-entrega">
                    {{ entrega.descricao }}
                  </p>
                  <div class="grid-entrega">
                    <label>
                      Status da entrega
                      <select v-model="entrega.situacao">
                        <option value="">Selecione</option>
                        <option
                          v-for="situacao in opcoesSituacoes"
                          :key="situacao"
                          :value="situacao"
                        >
                          {{ situacao }}
                        </option>
                      </select>
                    </label>
                    <label>
                      Progresso (%)
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        v-model.number="entrega.progresso"
                        @input="marcarEntregaManual(entrega)"
                        @blur="confirmarProgressoEntrega(acao, entrega)"
                      />
                    </label>
                  </div>
                  <label class="comentarios">
                    Comentários
                    <textarea
                      v-model="entrega.comentarios"
                      rows="2"
                      placeholder="Observações sobre a entrega"
                    ></textarea>
                  </label>
                  <div class="progresso-calculado entrega">
                    <small>
                      Calculado pelas tarefas:
                      <strong>
                        {{ formatarPercentual(entrega.__progressoCalculado) }}
                      </strong>
                    </small>
                    <button
                      type="button"
                      class="botao-link"
                      @click="usarProgressoCalculadoEntrega(acao, entrega)"
                      v-if="entrega.__progressoManual"
                    >
                      Usar valor calculado
                    </button>
                  </div>

                  <section class="secao-tarefas" v-if="entrega.tarefas.length">
                    <header class="secao-cabecalho interno">
                      <h5>Tarefas da entrega</h5>
                    </header>
                    <ul class="lista-tarefas">
                      <li v-for="tarefa in entrega.tarefas" :key="tarefa.idTarefa">
                        <div class="tarefa-cabecalho">
                          <span class="badge badge-tarefa">Tarefa</span>
                          <strong>{{ tarefa.titulo }}</strong>
                        </div>
                        <div class="tarefa-grid">
                          <label>
                            Status
                            <select
                              v-model="tarefa.situacao"
                              @change="
                                atualizarSituacaoTarefa(acao, entrega, tarefa)
                              "
                            >
                              <option value="">Selecione</option>
                              <option
                                v-for="situacao in opcoesSituacoes"
                                :key="situacao"
                                :value="situacao"
                              >
                                {{ situacao }}
                              </option>
                            </select>
                          </label>
                          <label>
                            Progresso (%)
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              v-model.number="tarefa.percentualConclusao"
                              @blur="
                                confirmarPercentualTarefa(acao, entrega, tarefa)
                              "
                            />
                          </label>
                          <div class="tarefa-informacoes">
                            <span>
                              Última mensuração:
                              {{
                                formatarPercentual(
                                  tarefa.ultimaMensuracao
                                    ? tarefa.ultimaMensuracao.percentualConclusao
                                    : null
                                )
                              }}
                            </span>
                            <small v-if="tarefa.ultimaMensuracao">
                              {{
                                formatarDataCurta(
                                  tarefa.ultimaMensuracao.dataReferencia
                                )
                              }}
                            </small>
                          </div>
                          <div class="tarefa-peso">
                            <span>Peso: {{ formatarPercentual(tarefa.peso) }}</span>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </section>
                </div>
              </article>
            </section>
          </div>
        </article>
      </section>

      <section class="bloco">
        <header class="bloco-header">
          <h3>Indicadores (KPIs)</h3>
          <small v-if="!form.metricas.length"
            >Nenhuma métrica cadastrada.</small
          >
        </header>
        <div
          v-for="metrica in form.metricas"
          :key="metrica.idMetrica"
          class="cartao-metrica"
        >
          <div class="cartao-cabecalho">
            <strong>{{ metrica.nome }}</strong>
            <div class="detalhes">
              <span>Meta: {{ formatarNumero(metrica.meta) }}</span>
              <span v-if="metrica.ultimaMensuracao"
                >Último valor:
                {{ formatarNumero(metrica.ultimaMensuracao.valorAtual) }} ({{
                  formatarDataCurta(metrica.ultimaMensuracao.dataReferencia)
                }})</span
              >
            </div>
          </div>
          <div class="cartao-conteudo">
            <label>
              Valor atual
              <input
                type="number"
                step="0.01"
                v-model.number="metrica.valorAtual"
              />
            </label>
            <label class="comentarios">
              Comentários
              <textarea
                v-model="metrica.comentarios"
                rows="2"
                placeholder="Observações sobre o indicador"
              ></textarea>
            </label>
          </div>
        </div>
      </section>
    </form>

    <section class="bloco" v-if="historico.length">
      <header class="bloco-header">
        <h3>Histórico de mensurações</h3>
        <small
          >Consulte as mensurações anteriores e acompanhe a evolução.</small
        >
      </header>
      <table class="tabela-historico">
        <thead>
          <tr>
            <th>Data</th>
            <th>Título</th>
            <th>Ações</th>
            <th>Indicadores</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in historico" :key="item.idMensuracao">
            <td>{{ formatarDataCurta(item.dataReferencia) }}</td>
            <td>{{ item.titulo || "Mensuração" }}</td>
            <td>{{ item.totalAcoes }}</td>
            <td>{{ item.totalMetricas }}</td>
            <td>
              <button
                type="button"
                class="botao-link"
                @click="abrirDetalhe(item)"
                :disabled="carregandoDetalhe"
              >
                Ver detalhes
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <div v-if="detalheSelecionado" class="modal" @click.self="fecharDetalhe">
      <div class="modal-caixa">
        <header>
          <h3>{{ detalheSelecionado.titulo || "Mensuração" }}</h3>
          <button type="button" class="fechar" @click="fecharDetalhe">×</button>
        </header>
        <p class="subtitulo">
          {{ formatarDataLonga(detalheSelecionado.dataReferencia) }}
        </p>
        <section>
          <h4>Ações</h4>
          <ul>
            <li v-for="acao in detalheSelecionado.acoes" :key="acao.idMarco">
              <strong>{{ acao.titulo || "Ação" }}</strong>
              <span>Status: {{ acao.situacao || "—" }}</span>
              <span
                v-if="acao.progresso !== null && acao.progresso !== undefined"
                >Progresso: {{ acao.progresso }}%</span
              >
              <p v-if="acao.comentarios">{{ acao.comentarios }}</p>
            </li>
          </ul>
        </section>
        <section
          v-if="detalheSelecionado.entregas && detalheSelecionado.entregas.length"
        >
          <h4>Entregas</h4>
          <ul class="historico-entregas">
            <li
              v-for="entrega in detalheSelecionado.entregas"
              :key="entrega.idEntrega"
            >
              <div class="historico-entrega-cabecalho">
                <strong>{{ entrega.titulo || "Entrega" }}</strong>
                <span>
                  {{ formatarPercentual(entrega.progresso) }} —
                  {{ formatarDataCurta(entrega.dataReferencia) }}
                </span>
              </div>
              <span>Status: {{ entrega.situacao || "—" }}</span>
              <p v-if="entrega.comentarios">{{ entrega.comentarios }}</p>
              <ul
                v-if="entrega.tarefas && entrega.tarefas.length"
                class="historico-entregas-tarefas"
              >
                <li
                  v-for="tarefa in entrega.tarefas"
                  :key="tarefa.idTarefa"
                >
                  <span class="titulo">{{ tarefa.titulo || "Tarefa" }}</span>
                  <span>Status: {{ tarefa.situacao || "—" }}</span>
                  <span>
                    Conclusão:
                    {{ formatarPercentual(tarefa.percentualConclusao) }}
                  </span>
                </li>
              </ul>
            </li>
          </ul>
        </section>
        <section>
          <h4>Indicadores</h4>
          <ul>
            <li
              v-for="metrica in detalheSelecionado.metricas"
              :key="metrica.idMetrica"
            >
              <strong>{{ metrica.nome || "Indicador" }}</strong>
              <span>Valor: {{ formatarNumero(metrica.valorAtual) }}</span>
              <p v-if="metrica.comentarios">{{ metrica.comentarios }}</p>
            </li>
          </ul>
        </section>
        <p v-if="detalheSelecionado.comentarios" class="comentarios-gerais">
          {{ detalheSelecionado.comentarios }}
        </p>
      </div>
    </div>
  </div>
  <div v-else class="carregando">Carregando mensuração...</div>
</template>

<script>
import {
  prepararMensuracaoProjeto,
  listarMensuracoesProjeto,
  registrarMensuracaoProjeto,
  obterMensuracaoProjeto,
  buscarOpcoes,
} from "../services/api";

export default {
  name: "MensuracaoProjeto",
  props: {
    idProjeto: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      carregando: false,
      carregandoDetalhe: false,
      salvando: false,
      erro: "",
      preparo: null,
      historico: [],
      opcoesSituacoes: [],
      detalheSelecionado: null,
      form: {
        dataReferencia: "",
        titulo: "",
        comentarios: "",
        situacaoProjeto: "",
        acoes: [],
        metricas: [],
      },
    };
  },
  computed: {
    nomePdm() {
      if (this.preparo && this.preparo.pdm && this.preparo.pdm.nome) {
        return this.preparo.pdm.nome;
      }
      const projeto = this.preparo && this.preparo.projeto;
      if (projeto && projeto.nomePdm) {
        return projeto.nomePdm;
      }
      return "";
    },
    progressoMedioProjeto() {
      if (!this.form || !Array.isArray(this.form.acoes)) {
        return this.normalizarPercentualEntrada(
          this.preparo && this.preparo.projeto
            ? this.preparo.projeto.progressoCalculado
            : null
        );
      }

      let totalPeso = 0;
      let acumulado = 0;

      this.form.acoes.forEach((acao) => {
        if (!acao) return;
        const progresso = this.normalizarPercentualEntrada(acao.progresso);
        if (progresso === null) return;

        const pesoEntregas = (acao.entregas || []).reduce(
          (total, entrega) => total + this.normalizarPesoEntrada(entrega.peso),
          0
        );
        const pesoUtil = pesoEntregas > 0 ? pesoEntregas : 100;

        totalPeso += pesoUtil;
        acumulado += progresso * pesoUtil;
      });

      if (totalPeso === 0) {
        return this.normalizarPercentualEntrada(
          this.preparo && this.preparo.projeto
            ? this.preparo.projeto.progressoCalculado
            : null
        );
      }

      return this.normalizarPercentualEntrada(acumulado / totalPeso);
    },
    progressoMedioPdm() {
      if (!this.preparo || !this.preparo.pdm) return null;

      const projetos = Array.isArray(this.preparo.pdm.projetos)
        ? this.preparo.pdm.projetos
        : [];

      let soma = 0;
      let contador = 0;

      projetos.forEach((projeto) => {
        if (!projeto || Number(projeto.idProjeto) === Number(this.idProjeto)) {
          return;
        }

        const valor = this.normalizarPercentualEntrada(projeto.progresso);
        if (valor === null) {
          return;
        }

        soma += valor;
        contador += 1;
      });

      const progressoAtual = this.progressoMedioProjeto;
      const valorAtual = this.normalizarPercentualEntrada(progressoAtual);

      if (valorAtual !== null) {
        soma += valorAtual;
        contador += 1;
      } else {
        const fallback =
          this.preparo && this.preparo.projeto
            ? this.normalizarPercentualEntrada(
                this.preparo.projeto.progressoCalculado
              )
            : null;
        if (fallback !== null) {
          soma += fallback;
          contador += 1;
        }
      }

      if (contador === 0) return null;

      return this.normalizarPercentualEntrada(soma / contador);
    },
  },
  watch: {
    idProjeto: {
      immediate: true,
      handler() {
        this.carregarDados();
      },
    },
  },
  methods: {
    async carregarDados() {
      this.erro = "";
      this.carregando = true;
      try {
        await Promise.all([
          this.carregarOpcoes(),
          this.carregarPreparo(),
          this.carregarHistorico(),
        ]);
      } catch (error) {
        this.erro = "Não foi possível carregar a mensuração do projeto.";
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] mensuracao", error);
      } finally {
        this.carregando = false;
      }
    },
    async carregarOpcoes() {
      try {
        const { data } = await buscarOpcoes();
        this.opcoesSituacoes = data.situacoes || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[gestao-projetos] mensuracao opcoes", error);
        this.opcoesSituacoes = [];
      }
    },
    alternarDetalhesAcao(acao) {
      if (!acao) return;
      acao.__colapsada = !acao.__colapsada;
    },
    alternarDetalhesEntrega(entrega) {
      if (!entrega) return;
      entrega.__colapsada = !entrega.__colapsada;
    },
    async carregarPreparo() {
      const { data } = await prepararMensuracaoProjeto(this.idProjeto);
      this.preparo = data;
      const hoje = new Date();
      this.form.dataReferencia = hoje.toISOString().substring(0, 10);
      this.form.titulo =
        data && data.projeto
          ? `Mensuração de ${data.projeto.titulo} (${hoje.toLocaleDateString(
              "pt-BR"
            )})`
          : "";
      this.form.comentarios = "";
      this.form.situacaoProjeto =
        (data && data.projeto && data.projeto.situacaoAtual) ||
        (data && data.projeto && data.projeto.situacao) ||
        "";
      this.form.acoes = (data.acoes || []).map((acao) => {
        const entregas = (acao.entregas || []).map((entrega) => {
          const ultimaEntrega = entrega.ultimaMensuracao || null;
          const tarefas = (entrega.tarefas || []).map((tarefa) => {
            const ultimaTarefa = tarefa.ultimaMensuracao || null;
            const percentual = this.valorDefinido(tarefa.percentualConclusao)
              ? this.normalizarPercentualEntrada(tarefa.percentualConclusao)
              : this.normalizarPercentualEntrada(
                  this.sugestaoPercentualPorSituacao(tarefa.situacaoAtual)
                );
            return {
              idTarefa: tarefa.idTarefa,
              titulo: tarefa.titulo,
              situacao: tarefa.situacaoAtual || "",
              peso: this.normalizarPesoEntrada(tarefa.peso),
              percentualConclusao: percentual,
              ultimaMensuracao: ultimaTarefa,
            };
          });

          const calculadoEntrega = this.calcularProgressoPonderado(
            tarefas,
            "percentualConclusao",
            "peso"
          );

          const progressoEntrega = this.valorDefinido(entrega.progressoAtual)
            ? this.normalizarPercentualEntrada(entrega.progressoAtual)
            : calculadoEntrega;

          return {
            idEntrega: entrega.idEntrega,
            titulo: entrega.titulo,
            descricao: entrega.descricao,
            situacao: entrega.situacaoAtual || "",
            peso: this.normalizarPesoEntrada(entrega.peso),
            progresso: progressoEntrega,
            comentarios: ultimaEntrega?.comentarios || "",
            tarefas,
            ultimaMensuracao: ultimaEntrega,
            __progressoCalculado: calculadoEntrega,
            __progressoManual: this.valorDefinido(entrega.progressoAtual),
            __colapsada: false,
          };
        });

        const calculadoAcao = this.calcularProgressoPonderado(
          entregas,
          "progresso",
          "peso"
        );

        let progressoAcao;
        if (this.valorDefinido(acao.progressoAtual)) {
          progressoAcao = this.normalizarPercentualEntrada(acao.progressoAtual);
        } else if (this.valorDefinido(calculadoAcao)) {
          progressoAcao = calculadoAcao;
        } else if (
          acao.ultimaMensuracao &&
          this.valorDefinido(acao.ultimaMensuracao.progresso)
        ) {
          progressoAcao = this.normalizarPercentualEntrada(
            acao.ultimaMensuracao.progresso
          );
        } else {
          progressoAcao = null;
        }

        return {
          idMarco: acao.idMarco,
          titulo: acao.titulo,
          descricao: acao.descricao,
          ultimaMensuracao: acao.ultimaMensuracao || null,
          situacao:
            acao.situacaoAtual ||
            (acao.ultimaMensuracao && acao.ultimaMensuracao.situacao) ||
            "",
          progresso: progressoAcao,
          comentarios: "",
          entregas,
          __progressoCalculado: calculadoAcao,
          __progressoManual: this.valorDefinido(acao.progressoAtual),
          __colapsada: false,
        };
      });
      this.form.metricas = (data.metricas || []).map((metrica) => ({
        idMetrica: metrica.idMetrica,
        nome: metrica.nome,
        meta: metrica.meta,
        ultimaMensuracao: metrica.ultimaMensuracao || null,
        valorAtual: this.valorDefinido(metrica.valorAtual)
          ? metrica.valorAtual
          : metrica.ultimaMensuracao &&
            this.valorDefinido(metrica.ultimaMensuracao.valorAtual)
          ? metrica.ultimaMensuracao.valorAtual
          : null,
        comentarios: "",
      }));
    },
    async carregarHistorico() {
      const { data } = await listarMensuracoesProjeto(this.idProjeto);
      this.historico = data || [];
    },
    classeObrigatorio(valor) {
      return !valor ? "obrigatorio" : "";
    },
    valorDefinido(valor) {
      return !(valor === null || valor === undefined || valor === "");
    },
    formatarNumero(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(valor));
    },
    formatarPercentual(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      const numero = Number(valor);
      if (Number.isNaN(numero)) return "—";
      return `${numero.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}%`;
    },
    normalizarPercentualEntrada(valor) {
      if (valor === null || valor === undefined || valor === "") return null;
      const numero = Number(valor);
      if (Number.isNaN(numero)) return null;
      let ajustado = numero;
      if (ajustado < 0) ajustado = 0;
      if (ajustado > 100) ajustado = 100;
      return Math.round(ajustado * 100) / 100;
    },
    normalizarPesoEntrada(valor) {
      if (valor === null || valor === undefined || valor === "") return 0;
      const numero = Number(valor);
      if (Number.isNaN(numero) || numero < 0) return 0;
      return Math.round(numero * 100) / 100;
    },
    sugestaoPercentualPorSituacao(situacao) {
      const texto = (situacao || "").toString().toLowerCase();
      if (!texto) return 0;
      const normalizado = texto
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s]/g, "")
        .replace(/\s+/g, " ")
        .trim();
      if (["concluida", "concluido", "finalizada", "finalizado"].includes(normalizado)) {
        return 100;
      }
      if (["execucao", "em execucao", "em andamento", "andamento"].includes(normalizado)) {
        return 50;
      }
      return 0;
    },
    calcularProgressoPonderado(itens, campoValor, campoPeso) {
      if (!Array.isArray(itens) || !itens.length) return 0;
      let somaPeso = 0;
      let somaPonderada = 0;
      let somaSemPeso = 0;
      let contSemPeso = 0;

      itens.forEach((item) => {
        if (!item) return;
        const valor = this.normalizarPercentualEntrada(item[campoValor]);
        if (valor === null) return;
        const peso = this.normalizarPesoEntrada(item[campoPeso]);
        if (peso > 0) {
          somaPeso += peso;
          somaPonderada += valor * peso;
        } else {
          somaSemPeso += valor;
          contSemPeso += 1;
        }
      });

      if (somaPeso > 0) {
        return this.normalizarPercentualEntrada(somaPonderada / somaPeso) ?? 0;
      }
      if (contSemPeso > 0) {
        return this.normalizarPercentualEntrada(somaSemPeso / contSemPeso) ?? 0;
      }
      return 0;
    },
    formatarDataCurta(valor) {
      if (!valor) return "—";
      return valueOfDate(valor).toLocaleDateString("pt-BR");
    },
    formatarDataLonga(valor) {
      if (!valor) return "—";
      return valueOfDate(valor).toLocaleString("pt-BR", { dateStyle: "full" });
    },
    async salvarMensuracao() {
      if (this.salvando) return;
      try {
        this.salvando = true;
        const payload = {
          dataReferencia: this.form.dataReferencia
            ? new Date(this.form.dataReferencia).toISOString()
            : null,
          titulo: this.form.titulo,
          comentarios: this.form.comentarios,
          situacao: this.form.situacaoProjeto,
          acoes: this.form.acoes.map((acao) => ({
            idMarco: acao.idMarco,
            situacao: acao.situacao,
            progresso: acao.__progressoManual
              ? this.normalizarPercentualEntrada(acao.progresso)
              : null,
            comentarios: acao.comentarios,
            entregas: (acao.entregas || []).map((entrega) => ({
              idEntrega: entrega.idEntrega,
              situacao: entrega.situacao,
              progresso: entrega.__progressoManual
                ? this.normalizarPercentualEntrada(entrega.progresso)
                : null,
              comentarios: entrega.comentarios,
              tarefas: (entrega.tarefas || []).map((tarefa) => ({
                idTarefa: tarefa.idTarefa,
                situacao: tarefa.situacao,
                percentualConclusao: this.normalizarPercentualEntrada(
                  tarefa.percentualConclusao
                ),
              })),
            })),
          })),
          metricas: this.form.metricas.map((metrica) => ({
            idMetrica: metrica.idMetrica,
            valorAtual: metrica.valorAtual,
            comentarios: metrica.comentarios,
          })),
        };
        await registrarMensuracaoProjeto(this.idProjeto, payload);
        this.$toasted &&
          this.$toasted.success("Mensuração registrada com sucesso!");
        await Promise.all([this.carregarPreparo(), this.carregarHistorico()]);
      } catch (error) {
        this.$toasted &&
          this.$toasted.error("Não foi possível salvar a mensuração.");
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] salvar mensuracao", error);
      } finally {
        this.salvando = false;
      }
    },
    marcarAcaoManual(acao) {
      if (!acao) return;
      acao.__progressoManual = true;
    },
    confirmarProgressoAcao(acao) {
      if (!acao) return;
      const valor = this.normalizarPercentualEntrada(acao.progresso);
      if (valor === null) {
        acao.__progressoManual = false;
        acao.progresso = acao.__progressoCalculado;
      } else {
        acao.progresso = valor;
      }
    },
    usarProgressoCalculadoAcao(acao) {
      if (!acao) return;
      acao.__progressoManual = false;
      acao.progresso = acao.__progressoCalculado;
    },
    marcarEntregaManual(entrega) {
      if (!entrega) return;
      entrega.__progressoManual = true;
    },
    confirmarProgressoEntrega(acao, entrega) {
      if (!entrega) return;
      const valor = this.normalizarPercentualEntrada(entrega.progresso);
      if (valor === null) {
        entrega.__progressoManual = false;
        entrega.progresso = entrega.__progressoCalculado;
      } else {
        entrega.progresso = valor;
      }
      this.recalcularAcao(acao);
    },
    usarProgressoCalculadoEntrega(acao, entrega) {
      if (!entrega) return;
      entrega.__progressoManual = false;
      entrega.progresso = entrega.__progressoCalculado;
      this.recalcularAcao(acao);
    },
    recalcularAcao(acao) {
      if (!acao) return;
      const calculado = this.calcularProgressoPonderado(
        acao.entregas || [],
        "progresso",
        "peso"
      );
      acao.__progressoCalculado = calculado;
      if (!acao.__progressoManual) {
        acao.progresso = calculado;
      }
    },
    recalcularEntrega(acao, entrega) {
      if (!entrega) return;
      const calculado = this.calcularProgressoPonderado(
        entrega.tarefas || [],
        "percentualConclusao",
        "peso"
      );
      entrega.__progressoCalculado = calculado;
      if (!entrega.__progressoManual) {
        entrega.progresso = calculado;
      }
      this.recalcularAcao(acao);
    },
    atualizarSituacaoTarefa(acao, entrega, tarefa) {
      if (!tarefa) return;
      if (!this.valorDefinido(tarefa.percentualConclusao)) {
        tarefa.percentualConclusao = this.normalizarPercentualEntrada(
          this.sugestaoPercentualPorSituacao(tarefa.situacao)
        );
      }
      this.recalcularEntrega(acao, entrega);
    },
    confirmarPercentualTarefa(acao, entrega, tarefa) {
      if (!tarefa) return;
      tarefa.percentualConclusao = this.normalizarPercentualEntrada(
        tarefa.percentualConclusao
      );
      this.recalcularEntrega(acao, entrega);
    },
    async abrirDetalhe(item) {
      try {
        this.carregandoDetalhe = true;
        const { data } = await obterMensuracaoProjeto(
          this.idProjeto,
          item.idMensuracao
        );
        this.detalheSelecionado = data;
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível carregar a mensuração selecionada."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] detalhe mensuracao", error);
      } finally {
        this.carregandoDetalhe = false;
      }
    },
    fecharDetalhe() {
      this.detalheSelecionado = null;
    },
  },
};

function valueOfDate(valor) {
  if (valor instanceof Date) return valor;
  return new Date(valor);
}
</script>

<style scoped>
.mensuracao-projeto {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.cabecalho h2 {
  margin: 0;
  font-size: 1.7rem;
  color: #0f172a;
}

.cabecalho .descricao {
  margin: 6px 0 0;
  color: #475569;
}

.cabecalho-acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.botao-primario,
.botao-secundario {
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}

.botao-primario {
  background: #1d4ed8;
  color: #ffffff;
}

.botao-secundario {
  background: transparent;
  border-color: #c7d2e2;
  color: #1f2937;
}

.voltar {
  display: inline-block;
  margin-bottom: 12px;
  color: #1d4ed8;
  font-weight: 600;
  text-decoration: none;
}

.formulario {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bloco {
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.resumo-mensuracao {
  gap: 12px;
}

.resumo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.resumo-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.resumo-item span {
  font-size: 0.85rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.resumo-item strong {
  font-size: 1.4rem;
  color: #0f172a;
}

.resumo-item small {
  color: #475569;
  font-size: 0.75rem;
}

.bloco-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.bloco-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #0f172a;
}

.linha {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.linha label {
  flex: 1 1 200px;
}

label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85rem;
  color: #334155;
}

input,
select,
textarea {
  border-radius: 8px;
  border: 1px solid #c7d2e2;
  padding: 8px 10px;
  font: inherit;
  color: #0f172a;
  background: #f8fafc;
}

textarea {
  resize: vertical;
}

.cartao-metrica {
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cartao {
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cartao + .cartao {
  margin-top: 16px;
}

.cartao.interno {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 14px;
  gap: 14px;
}

.cartao.interno + .cartao.interno {
  margin-top: 12px;
}

.cartao-cabecalho {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.cartao-identificacao {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.cartao-textos {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cartao-textos strong {
  color: #0f172a;
  font-size: 1rem;
}

.cartao-textos small {
  color: #475569;
  font-size: 0.8rem;
}

.descricao-acao {
  margin: 0;
  color: #475569;
  font-size: 0.85rem;
}

.alternar-colapso {
  border: none;
  background: transparent;
  color: #1d4ed8;
  font-weight: 600;
  cursor: pointer;
}

.alternar-colapso:hover {
  text-decoration: underline;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  color: #0f172a;
  background: #e2e8f0;
}

.badge-acao {
  background: rgba(59, 130, 246, 0.15);
  color: #1d4ed8;
}

.badge-entrega {
  background: rgba(16, 185, 129, 0.15);
  color: #0f766e;
}

.badge-tarefa {
  background: rgba(249, 115, 22, 0.15);
  color: #c2410c;
}

.cartao-conteudo {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.grid-acao,
.grid-entrega {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.grid-acao label,
.grid-entrega label {
  margin: 0;
}

.comentarios textarea {
  min-height: 80px;
}

.progresso-calculado {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  font-size: 0.85rem;
  color: #475569;
}

.progresso-calculado strong {
  color: #0f172a;
}

.secao-entregas {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.secao-cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.secao-cabecalho h4,
.secao-cabecalho h5 {
  margin: 0;
  font-size: 0.95rem;
  color: #0f172a;
}

.secao-cabecalho.interno h5 {
  font-size: 0.85rem;
}

.vazio.interno {
  font-size: 0.85rem;
  color: #64748b;
  background: #f8fafc;
  border: 1px dashed #cbd5f5;
  border-radius: 10px;
  padding: 12px;
}

.descricao-entrega {
  font-size: 0.85rem;
  color: #475569;
  margin: 0;
}

.secao-tarefas {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lista-tarefas {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lista-tarefas li {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tarefa-cabecalho {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tarefa-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.tarefa-grid label {
  margin: 0;
}

.tarefa-informacoes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
  color: #475569;
}

.tarefa-informacoes span {
  font-weight: 600;
  color: #0f172a;
}

.tarefa-peso {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #475569;
}

.cartao.colapsado .cartao-conteudo,
.cartao.interno.colapsado .cartao-conteudo {
  display: none;
}

.cartao-metrica .cartao-conteudo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.cartao-metrica .comentarios {
  grid-column: 1 / -1;
}

.obrigatorio {
  border-color: rgba(239, 68, 68, 0.6) !important;
}

.tabela-historico {
  width: 100%;
  border-collapse: collapse;
}

.tabela-historico th,
.tabela-historico td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.botao-link {
  border: none;
  background: transparent;
  color: #1d4ed8;
  font-weight: 600;
  cursor: pointer;
}

.modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 40;
}

.modal-caixa {
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 14px;
  padding: 20px;
  width: min(620px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modal-caixa header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-caixa h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
}

.modal-caixa .fechar {
  background: transparent;
  border: none;
  font-size: 1.6rem;
  cursor: pointer;
}

.modal-caixa section ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-caixa li {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  background: #f8fafc;
}

.modal-caixa .historico-entrega-cabecalho {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85rem;
  color: #475569;
  margin-bottom: 6px;
}

.modal-caixa .historico-entrega-cabecalho strong {
  color: #0f172a;
}

.modal-caixa .historico-entregas-tarefas {
  margin-top: 8px;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-caixa .historico-entregas-tarefas li {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 0.85rem;
  color: #475569;
}

.modal-caixa .historico-entregas-tarefas .titulo {
  font-weight: 600;
  color: #0f172a;
}

.comentarios-gerais {
  background: #eff6ff;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
}

.erro {
  border: 1px solid #fecaca;
  background: #fee2e2;
  color: #b91c1c;
  border-radius: 10px;
  padding: 12px;
}

.carregando {
  padding: 24px;
  text-align: center;
  color: #1d4ed8;
}

@media (max-width: 768px) {
  .cabecalho {
    flex-direction: column;
  }

  .cabecalho-acoes {
    width: 100%;
  }

  .cabecalho-acoes button {
    flex: 1 1 auto;
  }
}
</style>
